import { createApi } from '@reduxjs/toolkit/query/react';

import {
  GetEnrolleeInformationResponse,
  GetEZShipDeliveryFrequencyRequest,
  GetEZShipDeliveryFrequencyResponse,
  SubmitEnrollmentCheckoutRequest,
  SubmitEnrollmentCheckoutResponse,
  SubmitEnrollmentCheckoutV2Request,
  SubmitEnrollmentCheckoutV2Response,
  UpdateEnrollmentOrderSummaryRequest,
  UpdateEnrollmentOrderSummaryResponse,
} from '@/types/enrolment/EnrollmentApi';
import { SubmitEnrollmentCheckoutPayload } from '@/types/enrolment/Submit';
import { ResponseError } from '@/types/Error';
import {
  EnrollmentOrderDetails,
  JoinAddressInfo,
  UpdateOrderSummaryPayload,
  VerifyAddressResponse,
} from '@/types/joinUsType';
import { SuccessResponse } from '@/types/Success';

import { generateBaseQuseryWithReAuth } from '../baseQueryReauth';
import { setUpdateOrderSummary } from '../slices/enrollmentCheckout';

const contentTypes = {
  applicationJson: 'application/json',
};

export const enrollmentFlowApi = createApi({
  reducerPath: 'enrollmentFlowApi',
  keepUnusedDataFor: 300,

  baseQuery: generateBaseQuseryWithReAuth({ basePath: '' }),
  tagTypes: ['enrollment', 'enrolleInfo', 'updateEnrollment'],
  endpoints: builder => ({
    verifyAddress: builder.mutation<SuccessResponse<VerifyAddressResponse>, JoinAddressInfo>({
      query: payload => ({
        url: 'enrollment/verifyaddress',
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<VerifyAddressResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    validateWebalias: builder.mutation<SuccessResponse<unknown>, string>({
      query: payload => ({
        url: `enrollment/ValidateEnrollmentWebalias?webalias=${payload}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    validateSSN: builder.mutation<SuccessResponse<unknown>, string>({
      query: payload => ({
        url: `app/ValidateSSN/${payload}`,
        method: 'GET',
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<unknown>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
    updateEnrollmentSummary: builder.mutation<SuccessResponse<EnrollmentOrderDetails>, UpdateOrderSummaryPayload>({
      query: payload => ({
        url: `enrollment/UpdateEnrollmentOrderSummary`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: ['enrolleInfo'],
      transformResponse: (response: SuccessResponse<EnrollmentOrderDetails>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const resp = await queryFulfilled;
        const orderDetails = resp?.data?.data;
        dispatch(setUpdateOrderSummary(orderDetails));
      },
    }),

    updateEnrollmentOrderSummaryV2: builder.mutation<
      SuccessResponse<UpdateEnrollmentOrderSummaryResponse>,
      UpdateEnrollmentOrderSummaryRequest
    >({
      query: payload => ({
        url: `enrollment/updateenrollmentordersummary`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: ['enrolleInfo'],
      transformResponse: (response: SuccessResponse<UpdateEnrollmentOrderSummaryResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const resp = await queryFulfilled;
          const orderDetails = resp?.data?.data;
          if (orderDetails) {
            dispatch(setUpdateOrderSummary(orderDetails as unknown as EnrollmentOrderDetails));
          }
        } catch (error) {
          // Error handling is done by RTK Query
        }
      },
    }),

    getEnrolleInformation: builder.query<GetEnrolleeInformationResponse, number>({
      query: customerID => ({
        url: `Enrollment/?CustomerID=${customerID}`,
      }),
      providesTags: ['enrolleInfo'],
      transformResponse: (response: SuccessResponse<GetEnrolleeInformationResponse>, meta, arg) => response.data,
      transformErrorResponse: response => response as ResponseError,
    }),

    getEZShipDeliveryFrequency: builder.mutation<
      SuccessResponse<GetEZShipDeliveryFrequencyResponse>,
      GetEZShipDeliveryFrequencyRequest
    >({
      query: payload => ({
        url: `Enrollment/GetEZShipDeliveryFrequecy`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      invalidatesTags: ['enrolleInfo'],
      transformResponse: (response: SuccessResponse<GetEZShipDeliveryFrequencyResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    submitEnrollmentCheckout: builder.mutation<
      SuccessResponse<SubmitEnrollmentCheckoutResponse>,
      SubmitEnrollmentCheckoutRequest
    >({
      query: payload => ({
        url: `Enrollment/SubmitEnrollmentCheckout`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<SubmitEnrollmentCheckoutResponse>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    submitEnrollmentCheckoutLegacy: builder.mutation<SuccessResponse<string>, SubmitEnrollmentCheckoutPayload>({
      query: payload => ({
        url: `Enrollment/SubmitEnrollmentCheckout`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<string>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),

    submitEnrollmentCheckoutV2: builder.mutation<
      SuccessResponse<SubmitEnrollmentCheckoutV2Response>,
      SubmitEnrollmentCheckoutV2Request
    >({
      query: payload => ({
        url: `Enrollment/SubmitEnrollmentCheckout`,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': contentTypes.applicationJson,
        },
      }),
      transformResponse: (response: SuccessResponse<SubmitEnrollmentCheckoutV2Response>, meta, arg) => response,
      transformErrorResponse: response => response as ResponseError,
    }),
  }),
});

export const {
  useVerifyAddressMutation,
  useValidateSSNMutation,
  useValidateWebaliasMutation,
  useUpdateEnrollmentSummaryMutation: useUpdateEnrollmentOrderSummaryMutation,
  useUpdateEnrollmentOrderSummaryV2Mutation,
  useGetEnrolleInformationQuery,
  useLazyGetEnrolleInformationQuery,
  useGetEZShipDeliveryFrequencyMutation,
  useSubmitEnrollmentCheckoutMutation,
  useSubmitEnrollmentCheckoutLegacyMutation,
  useSubmitEnrollmentCheckoutV2Mutation,
} = enrollmentFlowApi;
